export type KanbanItem = {
  id: string;
  key: string;
  state_id: string;
  sort_order: number;
};

export function sortColumnItems(a: KanbanItem, b: KanbanItem): number {
  return a.sort_order - b.sort_order || a.key.localeCompare(b.key);
}

export function columnItemsForState<T extends KanbanItem>(
  items: T[],
  stateId: string,
): T[] {
  return items.filter((row) => row.state_id === stateId).sort(sortColumnItems);
}

/** Visual order: each column top-to-bottom, columns left-to-right. */
export function orderedItemKeys<T extends KanbanItem>(
  columns: ReadonlyArray<{ id: string }>,
  items: T[],
): string[] {
  const keys: string[] = [];
  for (const column of columns) {
    for (const item of columnItemsForState(items, column.id)) {
      keys.push(item.key);
    }
  }
  return keys;
}

export function sortItemsByColumn<T extends KanbanItem>(items: T[]): T[] {
  return [...items].sort(
    (a, b) => a.state_id.localeCompare(b.state_id) || sortColumnItems(a, b),
  );
}

export function neighboursForDrop<T extends KanbanItem>(
  column: T[],
  draggedId: string,
  insertBeforeId: string | null,
): { before_id?: string; after_id?: string } {
  const others = column.filter((item) => item.id !== draggedId);
  if (insertBeforeId == null) {
    const last = others[others.length - 1];
    return last ? { before_id: last.id } : {};
  }
  const idx = others.findIndex((item) => item.id === insertBeforeId);
  if (idx < 0) {
    const last = others[others.length - 1];
    return last ? { before_id: last.id } : {};
  }
  const before = others[idx - 1];
  const after = others[idx];
  return {
    ...(before ? { before_id: before.id } : {}),
    ...(after ? { after_id: after.id } : {}),
  };
}

export function previousBefore<T extends KanbanItem>(
  column: T[],
  itemId: string,
): string | null {
  const idx = column.findIndex((row) => row.id === itemId);
  if (idx <= 0) return null;
  return column[idx - 1]!.id;
}

export function previousAfter<T extends KanbanItem>(
  column: T[],
  itemId: string,
): string | null {
  const idx = column.findIndex((row) => row.id === itemId);
  if (idx < 0 || idx === column.length - 1) return null;
  return column[idx + 1]!.id;
}
